import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getDb, functions, getCallableName } from './firebase';

export interface FireRisk {
  code: string;
  countyId: string;
  countyName: string;
  rcm: number; // 1-5
  latitude: number;
  longitude: number;
}

export interface FireRiskForecast {
  forecastDate: string;
  executionDate: string;
  fileDate: string;
  forecast: { [code: string]: FireRisk };
}

/**
 * Get today's fire risk forecast
 */
export async function getTodayForecast(): Promise<FireRiskForecast | null> {
  try {
    const docRef = doc(getDb(), 'fire_risk', 'today');
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return docSnap.data() as FireRiskForecast;
  } catch (error) {
    console.error('Error fetching today forecast:', error);
    throw error;
  }
}

/**
 * Get tomorrow's fire risk forecast
 */
export async function getTomorrowForecast(): Promise<FireRiskForecast | null> {
  try {
    const docRef = doc(getDb(), 'fire_risk', 'tomorrow');
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return docSnap.data() as FireRiskForecast;
  } catch (error) {
    console.error('Error fetching tomorrow forecast:', error);
    throw error;
  }
}

/**
 * Trigger manual fire risk processing
 */
export async function triggerFireRiskProcessing(): Promise<{
  success: boolean;
  message: string;
  data?: { today: number; tomorrow: number };
}> {
  try {
    const triggerProcessing = httpsCallable(
      functions,
      getCallableName('triggerFireRiskProcessing')
    );
    const result = await triggerProcessing();
    return result.data as {
      success: boolean;
      message: string;
      data?: { today: number; tomorrow: number };
    };
  } catch (error) {
    console.error('Error triggering fire risk processing:', error);
    throw error;
  }
}

/**
 * Trigger manual dispatch of pending fire risk notifications
 */
export async function triggerNotificationDispatch(): Promise<{
  success: boolean;
  processed: number;
  sent: number;
  failed: number;
  message?: string;
}> {
  try {
    const dispatch = httpsCallable(functions, getCallableName('triggerNotificationDispatch'));
    const result = await dispatch();
    return result.data as {
      success: boolean;
      processed: number;
      sent: number;
      failed: number;
      message?: string;
    };
  } catch (error) {
    console.error('Error triggering notification dispatch:', error);
    throw error;
  }
}

/**
 * Get risk level label and badge class
 */
export function getRiskLevelInfo(rcm: number): { label: string; badge: string; color: string } {
  switch (rcm) {
    case 1:
      return { label: 'Low', badge: 'bg-success', color: '#28a745' };
    case 2:
      return { label: 'Moderate', badge: 'bg-info', color: '#17a2b8' };
    case 3:
      return { label: 'High', badge: 'bg-warning', color: '#ffc107' };
    case 4:
      return { label: 'Very High', badge: 'bg-orange', color: '#fd7e14' };
    case 5:
      return { label: 'Maximum', badge: 'bg-danger', color: '#dc3545' };
    default:
      return { label: 'Unknown', badge: 'bg-secondary', color: '#6c757d' };
  }
}

/**
 * Get statistics from forecast data
 */
export function getFireRiskStats(forecast: FireRiskForecast | null): {
  total: number;
  byLevel: { level: number; count: number; label: string }[];
} {
  if (!forecast || !forecast.forecast) {
    return { total: 0, byLevel: [] };
  }

  const municipalities = Object.values(forecast.forecast);
  const total = municipalities.length;

  // Count by risk level
  const levelCounts = new Map<number, number>();
  municipalities.forEach(mun => {
    levelCounts.set(mun.rcm, (levelCounts.get(mun.rcm) || 0) + 1);
  });

  const byLevel = Array.from(levelCounts.entries())
    .map(([level, count]) => ({
      level,
      count,
      label: getRiskLevelInfo(level).label,
    }))
    .sort((a, b) => b.level - a.level);

  return { total, byLevel };
}
